import type { ReactNode } from "react";
import type { PatientCoding } from "../api/claimsApi";

type Props = {
  patients: PatientCoding[];
  currency?: string;
  /** Contenu ajouté sous le nom du patient (codes d'affection côté Audit). */
  renderExtra?: (patient: PatientCoding) => ReactNode;
};

function amount(value: number): string {
  return value.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/**
 * Actes de chaque patient regroupés par code standard (RCS, RB, RPPH…), avec les réfactions codées (COR…) :
 * l'équivalent des annotations du médecin sur la facture détaillée.
 */
export function PatientCodingSummary({ patients, currency = "", renderExtra }: Props) {
  if (patients.length === 0) return <p className="text-sm text-muted">Aucun acte codé.</p>;

  return (
    <div className="grid gap-3 md:grid-cols-2">
      {patients.map((patient) => (
        <div key={patient.beneficiary ?? "none"} className="rounded-lg border border-border p-3">
          <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
            <p className="text-sm font-semibold text-foreground">
              {patient.name}
              {patient.matricule ? <span className="ml-1 font-normal text-muted">· {patient.matricule}</span> : null}
              {patient.company ? <span className="ml-1 font-normal text-muted">· {patient.company}</span> : null}
            </p>
            {patient.agent ? <span className="text-xs text-muted">ayant droit de {patient.agent}</span> : null}
          </div>
          {renderExtra ? renderExtra(patient) : null}
          <table className="w-full text-sm">
            <tbody>
              {patient.codes.map((code) => (
                <tr key={code.code} className="border-t border-border first:border-t-0">
                  <td className="py-1 pr-2 font-mono text-xs font-semibold text-primary" title={code.name}>
                    {code.code}
                  </td>
                  <td className="py-1 pr-2 text-xs text-muted">{code.lines} acte(s)</td>
                  <td className="py-1 text-right tabular-nums">{amount(code.billed)}</td>
                </tr>
              ))}
              {patient.refactions.map((refaction) => (
                <tr key={`r-${refaction.code}`} className="border-t border-border text-red-700">
                  <td className="py-1 pr-2 font-mono text-xs font-semibold">{refaction.code}</td>
                  <td className="py-1 pr-2 text-xs">réfaction</td>
                  <td className="py-1 text-right tabular-nums">−{amount(refaction.amount)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-border text-xs">
                <td colSpan={2} className="pt-1 text-muted">
                  Facturé {amount(patient.billed)} · réfacté {amount(patient.refracted)}
                  {patient.covered !== undefined && Math.abs(patient.covered - patient.retained) > 0.004
                    ? ` · hors couverture ${amount(patient.retained - patient.covered)}`
                    : ""}
                </td>
                <td className="pt-1 text-right font-semibold tabular-nums text-foreground">
                  {amount(patient.covered ?? patient.retained)} {currency}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      ))}
    </div>
  );
}
